// src/pages/talentScout/getTeam.js

import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ProfileImage from "../../components/profileImage";

const API_URL = "https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1";

const GetTeam = () => {
  const { t } = useTranslation();
  const [team, setTeam] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const { id: teamId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTeamData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("Authentication token not found");
        }
        const response = await fetch(
          `${API_URL}/talent-scout/getTeamById?teamId=${teamId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.errors || `Failed to fetch team data: ${response.status}`
          );
        }

        const data = await response.json();
        setTeam(data.team);
        setTeamMembers(data.teamMembers || []);
        setOrganization(data.organization);
      } catch (err) {
        console.error("Error fetching team data:", err);
        setError(err.message || "Failed to fetch team data");
      } finally {
        setLoading(false);
      }
    };

    if (teamId) {
      fetchTeamData();
    } else {
      setError("Team ID is required");
      setLoading(false);
    }
  }, [teamId]);

  const getTeamAge = () => {
    if (!team?.createdAt) return "N/A";
    const created = new Date(team.createdAt);
    const now = new Date();
    const diffTime = Math.abs(now - created);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 30) return `${diffDays} days`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months`;
    return `${Math.floor(diffDays / 365)} years`;
  };

  const players = teamMembers.filter(member => member.type === "player");
  const supervisors = teamMembers.filter(member => member.type === "supervisor");

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-orange-50 to-red-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-orange-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">{t("talentScout.loadingTeamDetails")}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-red-50 to-pink-100">
        <div className="text-center bg-white p-8 rounded-xl shadow-lg">
          <div className="text-red-500 text-xl font-semibold mb-2">⚠️ {t("talentScout.error")}</div>
          <div className="text-gray-600 mb-4">{error}</div>
          <button 
            onClick={() => navigate(-1)}
            className="px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium"
          >
            ← {t("talentScout.goBack")}
          </button>
        </div>
      </div>
    );
  }

  if (!team) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center bg-white p-8 rounded-xl shadow-lg">
          <div className="text-xl font-semibold mb-2">🏆 Team Not Found</div>
          <div className="text-gray-600 mb-4">{t("talentScout.requestedTeamNotFound")}</div>
          <button 
            onClick={() => navigate(-1)}
            className="px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium"
          >
            ← {t("talentScout.goBack")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="mb-6 flex items-center text-gray-600 hover:text-gray-900 transition-colors"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
                     {t("talentScout.backToSearch")}
        </button>

        {/* Hero Section */}
        <div className="bg-gradient-to-r from-orange-600 to-red-600 rounded-2xl shadow-xl overflow-hidden mb-8" style={{background: "linear-gradient(135deg, #EC6B37 0%, #d55a2e 100%)"}}>
          <div className="p-8 text-white">
            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-8">
              <div className="flex-shrink-0">
                <img
                  src={team.logo}
                  alt={team.name}
                  className="w-32 h-32 rounded-2xl object-cover ring-4 ring-white/20"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div 
                  className="w-32 h-32 rounded-2xl bg-gradient-to-br from-white/20 to-white/10 flex items-center justify-center text-white font-bold text-3xl ring-4 ring-white/20"
                  style={{ display: 'none' }}
                >
                  {team.name?.charAt(0)?.toUpperCase() || 'T'}
                </div>
              </div>
              <div className="flex-grow">
                <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
                  <div>
                    <h1 className="text-4xl font-bold mb-2">{team.name}</h1>
                    <p className="text-orange-100 text-lg mb-4">@{team.uniqueName}</p>
                    <div className="flex flex-wrap gap-3">
                      <span className="bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full text-sm">
                        🏆 {team.sport}
                      </span>
                      <span className="bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full text-sm">
                        👥 {team.gender} Team
                      </span>
                      <span className="bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full text-sm">
                        📅 {getTeamAge()} old
                      </span>
                      <span className="bg-emerald-500/20 backdrop-blur-sm px-3 py-1 rounded-full text-sm">
                        ✓ {team.type}
                      </span>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                                 <p className="text-sm text-gray-500 font-medium">{t("talentScout.totalMembers")}</p>
                <p className="text-3xl font-bold text-gray-900">{teamMembers.length}</p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                                 <p className="text-sm text-gray-500 font-medium">{t("talentScout.players")}</p>
                <p className="text-3xl font-bold text-gray-900">{players.length}</p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                                 <p className="text-sm text-gray-500 font-medium">{t("talentScout.supervisors")}</p>
                <p className="text-3xl font-bold text-gray-900">{supervisors.length}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                                 <p className="text-sm text-gray-500 font-medium">{t("talentScout.teamAge")}</p>
                <p className="text-3xl font-bold text-gray-900">{getTeamAge()}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {["overview", "members", "organization"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab
                      ? "border-orange-500 text-orange-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  {t(`talentScout.${tab}`)}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === "overview" && (
              <div className="space-y-8">
                {/* Team Details */}
                <div>
                                     <h3 className="text-xl font-semibold mb-4">{t("talentScout.teamInformation")}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                                             <p className="text-sm text-gray-500 font-medium">{t("talentScout.uniqueName")}</p>
                      <p className="text-lg font-semibold text-gray-900">@{team.uniqueName}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                                             <p className="text-sm text-gray-500 font-medium">{t("talentScout.sport")}</p>
                      <p className="text-lg font-semibold text-gray-900">{team.sport}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                                             <p className="text-sm text-gray-500 font-medium">{t("talentScout.genderCategory")}</p>
                      <p className="text-lg font-semibold text-gray-900 capitalize">{team.gender}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                                             <p className="text-sm text-gray-500 font-medium">{t("talentScout.teamType")}</p>
                      <p className="text-lg font-semibold text-gray-900">{team.type}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                                             <p className="text-sm text-gray-500 font-medium">{t("talentScout.createdDate")}</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {new Date(team.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                                             <p className="text-sm text-gray-500 font-medium">{t("talentScout.teamStatus")}</p>
                      <p className="text-lg font-semibold text-green-600">{t("talentScout.active")}</p>
                    </div>
                  </div>
                </div>

                {/* Team Analytics */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-6 rounded-xl">
                                         <h4 className="font-semibold text-orange-900 mb-4">{t("talentScout.teamComposition")}</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                                                 <span className="text-orange-700">{t("talentScout.totalMembers")}:</span>
                        <span className="font-medium text-orange-900">{teamMembers.length}</span>
                      </div>
                      <div className="flex justify-between items-center">
                                                 <span className="text-orange-700">{t("talentScout.players")}:</span>
                        <span className="font-medium text-orange-900">{players.length} ({Math.round((players.length / teamMembers.length) * 100)}%)</span>
                      </div>
                      <div className="flex justify-between items-center">
                                                 <span className="text-orange-700">{t("talentScout.supervisors")}:</span>
                        <span className="font-medium text-orange-900">{supervisors.length} ({Math.round((supervisors.length / teamMembers.length) * 100)}%)</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 p-6 rounded-xl">
                                         <h4 className="font-semibold text-emerald-900 mb-4">{t("talentScout.teamMetrics")}</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                                                 <span className="text-emerald-700">{t("talentScout.teamAge")}:</span>
                        <span className="font-medium text-emerald-900">{getTeamAge()}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-emerald-700">{t("category")}:</span>
                        <span className="font-medium text-emerald-900">{team.gender} {team.sport}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-emerald-700">{t("team_type")}:</span>
                        <span className="font-medium text-emerald-900">{team.type}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Members Tab */}
            {activeTab === "members" && (
              <div className="space-y-8">
                {/* Players Section */}
                <div>
                                     <h3 className="text-xl font-semibold mb-6">{t("talentScout.players")} ({players.length})</h3>
                  {players.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                                             <p className="text-gray-500 text-lg">{t("talentScout.noPlayersFound")}</p>
                       <p className="text-gray-400 text-sm">{t("talentScout.noPlayersAdded")}</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {players.map((player) => (
                        <Link
                          key={player.userId}
                          to={`/talent-scout/get-player/${player.userId}`}
                          className="group bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg hover:border-orange-300 transition-all duration-200"
                        >
                          <div className="flex items-center space-x-4">
                            <ProfileImage
                              image={player.profilePicture}
                              alt={player.userName}
                              className="w-16 h-16 rounded-lg object-cover ring-2 ring-gray-100 group-hover:ring-orange-200 transition-all"
                            />
                            <div className="flex-grow min-w-0">
                              <h4 className="font-semibold text-gray-900 group-hover:text-orange-600 transition-colors truncate">
                                {player.userFirstName} {player.userLastName}
                              </h4>
                              <p className="text-sm text-gray-500 truncate">@{player.userName}</p>
                              <p className="text-xs text-gray-400 mt-1">
                                {t("talentScout.joined")}: {new Date(player.joinedAt).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                {/* Supervisors Section */}
                <div>
                  <h3 className="text-xl font-semibold mb-6">{t("talentScout.supervisors")} ({supervisors.length})</h3>
                  {supervisors.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-1a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                      </div>
                      <p className="text-gray-500 text-lg">{t("talentScout.noSupervisorsFound")}</p>
                      <p className="text-gray-400 text-sm">{t("talentScout.noSupervisorsAssigned")}</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {supervisors.map((supervisor) => (
                        <div
                          key={supervisor.userId}
                          className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm"
                        >
                          <div className="flex items-center space-x-4">
                            <ProfileImage
                              image={supervisor.profilePicture}
                              alt={supervisor.userName}
                              className="w-16 h-16 rounded-lg object-cover ring-2 ring-purple-100"
                            />
                            <div className="flex-grow min-w-0">
                              <h4 className="font-semibold text-gray-900 truncate">
                                {supervisor.userFirstName} {supervisor.userLastName}
                              </h4>
                              <p className="text-sm text-gray-500 truncate">@{supervisor.userName}</p>
                              <p className="text-xs text-purple-600 font-medium mt-1">{supervisor.title || t("talentScout.supervisor")}</p>
                              <p className="text-xs text-gray-400">
                                {t("talentScout.joined")}: {new Date(supervisor.joinedAt).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Organization Tab */}
            {activeTab === "organization" && (
              <div className="space-y-6">
                {organization ? (
                  <div className="bg-white border border-gray-200 rounded-xl p-8">
                    <div className="flex items-start gap-6">
                      <ProfileImage
                        image={organization.profilePicture}
                        alt={organization.userName}
                        className="w-24 h-24 rounded-xl shadow-lg"
                      />
                      <div className="flex-grow">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-1">
                              {organization.firstName} {organization.lastName}
                            </h3>
                            <p className="text-orange-600 font-medium">@{organization.userName}</p>
                          </div>
                          <Link
                            to={`/talent-scout/get-organization/${organization._id}`}
                            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium"
                          >
                            {t("view_details")}
                          </Link>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <p className="text-sm text-gray-500 font-medium">{t("email")}</p>
                            <p className="text-gray-900 font-semibold">{organization.email}</p>
                          </div>
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <p className="text-sm text-gray-500 font-medium">{t("phone")}</p>
                            <p className="text-gray-900 font-semibold">{organization.phone || "Not provided"}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                    <p className="text-gray-500 text-lg">{t("no_organization_information")}</p>
                    <p className="text-gray-400 text-sm">{t("organization_details_not_available")}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GetTeam;
